import api from '@/services/api';

const STUDIO_BASE = '/ai-studio';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export interface InstanceMonitorDto {
  instanceId: string;
  instanceName: string;
  agentName: string;
  agentCode: string;
  versionNumber: number;
  setupStatus: {
    status: string;
    configured: boolean;
    messages: string[];
  } | null;
  upgradeAvailable: boolean;
  upgradeCount: number;
  lastRunAt: string | null;
  failedRunCount: number;
  recentRuns: Array<{
    id: string;
    run_name: string;
    status: string;
    created_at: string;
    updated_at: string;
    owner_email: string | null;
  }>;
}

export interface TraceEvent {
  type: string;
  at: string;
  label: string;
  detail?: Record<string, unknown>;
}

export interface RunExecutionTrace {
  run: {
    id: string;
    run_name: string;
    status: string;
    agent_name: string;
    version_number: number;
    binding_name: string | null;
    created_at: string;
    updated_at: string;
  };
  timeline: TraceEvent[];
  summary: {
    duration_ms: number;
    total_tokens: number;
    tool_call_count: number;
    compute_count: number;
    event_count: number;
  };
}

export interface StateCommitDto {
  id: string;
  commitKind: string;
  version: number;
  committedAt: string;
  changeSummary: Record<string, unknown>;
}

function mapMonitor(row: Record<string, unknown>): InstanceMonitorDto {
  const setup = row['setup_status'] as Record<string, unknown> | null;
  return {
    instanceId: String(row['instance_id']),
    instanceName: String(row['instance_name']),
    agentName: String(row['agent_name']),
    agentCode: String(row['agent_code']),
    versionNumber: Number(row['version_number']),
    setupStatus: setup
      ? {
          status: String(setup['status']),
          configured: Boolean(setup['configured']),
          messages: Array.isArray(setup['messages']) ? setup['messages'].map(String) : [],
        }
      : null,
    upgradeAvailable: Boolean(row['upgrade_available']),
    upgradeCount: Number(row['upgrade_count'] ?? 0),
    lastRunAt: row['last_run_at'] ? String(row['last_run_at']) : null,
    failedRunCount: Number(row['failed_run_count'] ?? 0),
    recentRuns: Array.isArray(row['recent_runs']) ? (row['recent_runs'] as InstanceMonitorDto['recentRuns']) : [],
  };
}

function mapCommit(row: Record<string, unknown>): StateCommitDto {
  return {
    id: String(row['id']),
    commitKind: String(row['commit_kind']),
    version: Number(row['version']),
    committedAt: String(row['committed_at']),
    changeSummary:
      row['change_summary'] && typeof row['change_summary'] === 'object'
        ? (row['change_summary'] as Record<string, unknown>)
        : {},
  };
}

export async function getInstanceMonitor(instanceId: string): Promise<InstanceMonitorDto> {
  const response = await api.get<ApiEnvelope<Record<string, unknown>>>(
    `${STUDIO_BASE}/instances/${instanceId}/monitor`
  );
  return mapMonitor(response.data.data);
}

export async function getRunExecutionTrace(runId: string): Promise<RunExecutionTrace> {
  const response = await api.get<ApiEnvelope<RunExecutionTrace>>(
    `${STUDIO_BASE}/runs/${runId}/trace`
  );
  return response.data.data;
}

export async function listInstanceSetupHistory(instanceId: string): Promise<StateCommitDto[]> {
  const response = await api.get<ApiEnvelope<Record<string, unknown>[]>>(
    `${STUDIO_BASE}/instances/${instanceId}/history/setup`
  );
  return response.data.data.map(mapCommit);
}

export async function listInstanceOutputsHistory(instanceId: string): Promise<StateCommitDto[]> {
  const response = await api.get<ApiEnvelope<Record<string, unknown>[]>>(
    `${STUDIO_BASE}/instances/${instanceId}/history/outputs`
  );
  return response.data.data.map(mapCommit);
}

export async function listMyRuns(options?: {
  instanceId?: string;
  limit?: number;
}): Promise<Record<string, unknown>[]> {
  const params = new URLSearchParams();
  if (options?.instanceId) params.set('instance_id', options.instanceId);
  if (options?.limit) params.set('limit', String(options.limit));
  const qs = params.toString();
  const response = await api.get<ApiEnvelope<Record<string, unknown>[]>>(
    `${STUDIO_BASE}/runs/mine${qs ? `?${qs}` : ''}`
  );
  return response.data.data;
}
